<template>
  <div class="project-measurement-report" v-if="project">
    <b-row v-if="showToGuest('measurementReport', guestVisibleSections)">
      <b-col lg="8" md="8" sm="12">
        <legend>
          <strong>{{ $t('measurementReport') }}: {{ $t('generate_report') }}</strong>
        </legend>
        <b-row>
          <b-col lg="4" md="7" sm="7">
            <AttachmentList
              :attachments="project.measurementReportAttachments"
              :attachmentType="attachmentType"
              v-on:attachment-list:delete="onDeleteAttachment"
            ></AttachmentList>
          </b-col>
          <b-col lg="8" md="5" sm="5">
            <router-link
              class="btn btn-success pull-right"
              :to="{
                name: 'MeasurementPrintPage',
                params: { projectNumber: project.number },
              }"
              target="_blank"
            >
              <i class="fa fa-file mr-2"></i>{{ $t('preview') }}
            </router-link>
            <ButtonWait
              v-if="!isGuest"
              :startCallback="startReportBuild"
              :pollCallback="fetchBuildState"
              class="pull-right mr-2"
            >
              <template v-slot:buttonText>
                {{ $t('generate_report') }}
              </template>
            </ButtonWait>
          </b-col>
        </b-row>
      </b-col>
      <b-col lg="4" md="4" sm="12" v-if="showToGuest('downloads', guestVisibleSections)">
        <legend>
          <strong>{{ $t('downloads') }}</strong>
        </legend>
        <b-row>
          <b-col lg="10" md="10" sm="10">
            {{ $t('measurementReportImageAttachments') }}
          </b-col>
          <b-col lg="2" md="2" sm="2">
            <ZipDownload
              attachmentType="measurementReportImageAttachments"
              :attachments="project.measurementReportImageAttachmentsZip"
              :project="project"
            />
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <b-row class="mt-4" v-if="showToGuest('measurementVerification', guestVisibleSections)">
      <b-col lg="12" md="12" sm="12">
        <legend>
          <strong>{{ $t('measurementReport') }}: {{ $t('verification') }}</strong>
        </legend>
        <div class="verification-flex m-1">
          <VerificationState
            :project="project"
            verificationStateType="measurementAcVerificationState"
          />
          <VerificationState
            :project="project"
            verificationStateType="measurementDcVerificationState"
          />
          <VerificationState
            v-if="
              project.measurementVerificationState &&
                project.measurementVerificationState !== 'UNAUDITED'
            "
            :project="project"
            verificationStateType="measurementVerificationState"
            :readonly="true"
          />
        </div>
      </b-col>
    </b-row>
    <b-row class="mt-4" v-if="showToGuest('measurementApprovement', guestVisibleSections)">
      <b-col lg="12" md="12" sm="12">
        <legend>
          <strong>{{ $t('measurementReport') }}: {{ $t('approvement') }}</strong>
        </legend>
        <div class="verification-flex m-1">
          <VerificationState
            :project="project"
            verificationStateType="measurementApprovementVerificationState"
          />
          <Card v-if="project.measurement">
            <template #title>{{ $t('measurementApprovementNote') }} </template>
            <template #content>
              <b-form-textarea
                id="description"
                v-model="project.measurement.measurementApprovementNote"
                placeholder="..."
                rows="4"
                max-rows="4"
                :disabled="!canWriteField('measurementApprovementNote', guestWritableFields)"
              ></b-form-textarea>
            </template>
          </Card>
        </div>
      </b-col>
    </b-row>
    <b-row class="mt-4" v-if="showToGuest('measurementData', guestVisibleSections)">
      <b-col lg="12" md="12" sm="12">
        <legend>
          <strong>{{ $t('measurementReport') }}: {{ $t('collected_data') }}</strong>
        </legend>
        <MobileProjectsMeasurement :project="project" />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import MobileProjectsMeasurement from '@/views/Projects/MobileProjectsMeasurement';
import VerificationState from '@/components/VerificationState.vue';
import ButtonWait from '@/components/ButtonWait.vue';
import AttachmentList from '@/components/AttachmentList';
import Card from 'primevue/card';
import { showToGuest, canWriteField } from '@/helpers/userCapabilities';
import ZipDownload from '@/components/ZipDownload';

export default {
  components: {
    ButtonWait,
    AttachmentList,
    MobileProjectsMeasurement,
    VerificationState,
    Card,
    ZipDownload,
  },
  props: {
    project: { type: Object },
  },
  data() {
    return {
      attachmentType: 'measurementReportAttachments',
      guestWritableFields: {
        measurementApprovementNote: true,
      },
      guestVisibleSections: {
        measurementReport: true,
        measurementVerification: true,
        measurementApprovement: true,
        measurementData: true,
      },
    };
  },
  computed: {
    ...mapGetters('auth', ['isGuest']),
  },
  methods: {
    ...mapActions(['buildMeasurementReport', 'fetchMeasurementReportAttachmentsBuildState']),

    /**
     * Start pdf build on server
     * Is provided to ButtonWait as callback function
     */
    async startReportBuild() {
      if (this.project) {
        await this.buildMeasurementReport(this.project.id);
      }
    },
    /**
     * Fetch build state from server (get's called once per second)
     * Is provided to ButtonWait as callback function
     */
    fetchBuildState() {
      this.fetchMeasurementReportAttachmentsBuildState(this.project.number);
      return this.project.measurementReportAttachmentsBuildState;
    },
    /**
     * Bubble up AttachmentList delete event
     */
    onDeleteAttachment(event) {
      this.$emit('project-measurement-report-attachment-list:delete', {
        attachmentId: event.attachmentId,
        attachmentType: this.attachmentType,
      });
    },
    canWriteField,
    showToGuest,
  },
  mounted() {},
};
</script>

<style lang="scss" scoped>
.verification-flex {
  display: flex;
  gap: 1em;
  flex-flow: row wrap;
  flex-basis: 100%;
  justify-content: flex-start;
}

.verification-flex > div {
  flex: 1;
  min-width: 300px;
}
</style>
